import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; 
import { SettingsContext } from "../context/SettingsContext"; 

// Heroicons (Updated Set with HR Icons & Modern Vector Icons)
import { 
  CubeIcon, 
  ChevronDownIcon, 
  ChevronRightIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ReceiptPercentIcon,
  BuildingLibraryIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  ScaleIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  TrashIcon,
  UsersIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  BuildingOffice2Icon,
  Squares2X2Icon
} from "@heroicons/react/24/outline";

// Construction HardHat Icon with currentColor support (matches original Briq Project icon)
const HardHatIcon = ({ className = "h-5 w-5 shrink-0" }) => (
  <svg viewBox="0 0 30 30" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M12.5 12.5V6.25C12.5 5.91848 12.6317 5.60054 12.8661 5.36612C13.1005 5.1317 13.4185 5 13.75 5H16.25C16.5815 5 16.8995 5.1317 17.1339 5.36612C17.3683 5.60054 17.5 5.91848 17.5 6.25V12.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17.5 7.5C19.4891 7.5 21.3968 8.29018 22.8033 9.6967C24.2098 11.1032 25 13.0109 25 15V18.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 18.75V15C5 13.0109 5.79018 11.1032 7.1967 9.6967C8.60322 8.29018 10.5109 7.5 12.5 7.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M26.25 18.75H3.75C3.05964 18.75 2.5 19.3096 2.5 20V22.5C2.5 23.1904 3.05964 23.75 3.75 23.75H26.25C26.9404 23.75 27.5 23.1904 27.5 22.5V20C27.5 19.3096 26.9404 18.75 26.25 18.75Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// --- LINK COMPONENT ---
const SidebarLink = ({ name, icon, IconComponent, path, active, scrollContainerRef, closeSidebar }) => {
  const handleClick = () => {
    if (scrollContainerRef?.current) {
      const scrollPos = scrollContainerRef.current.scrollTop;
      sessionStorage.setItem('sidebar_scroll', scrollPos.toString());
    }
    // Only close the sidebar if we are on a mobile screen
    if (window.innerWidth < 1024 && closeSidebar) {
      closeSidebar();
    }
  };

  const IconToRender = IconComponent || (icon ? () => <img src={icon} alt={name} className="h-5 w-5 shrink-0" /> : null);

  return (
    <Link
      to={path}
      onClick={handleClick}
      aria-current={active ? "page" : undefined}
      className={`group flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 ${
        active
          ? "bg-slate-100 dark:bg-[#21262d] text-slate-900 dark:text-white font-semibold shadow-2xs"
          : "text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-[#21262d]/60"
      }`}
    >
      {IconToRender && (
        <IconToRender className={`h-5 w-5 shrink-0 transition-colors ${active ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-[#6e7681] group-hover:text-slate-700 dark:group-hover:text-slate-300"}`} />
      )}
      <span className="truncate">{name}</span>
    </Link>
  );
};

// --- GROUP COMPONENT ---
const SidebarGroup = ({ group, location, scrollContainerRef, closeSidebar }) => {
  const isAnyChildActive = group.children?.some(child => location.pathname.startsWith(child.path.split('?')[0]));
  const [isOpen, setIsOpen] = useState(isAnyChildActive);

  useEffect(() => {
    if (isAnyChildActive) setIsOpen(true);
  }, [isAnyChildActive]);

  return (
    <div className="space-y-0.5">
      <div 
        className={`group flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer text-sm font-medium transition-all duration-150 ${
          isAnyChildActive 
            ? "bg-slate-50 dark:bg-[#21262d]/60 text-slate-900 dark:text-white font-semibold" 
            : "text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-[#21262d]/60"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {group.IconComponent ? (
            <group.IconComponent className={`h-5 w-5 shrink-0 transition-colors ${isAnyChildActive ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-[#6e7681] group-hover:text-slate-700 dark:group-hover:text-slate-300"}`} />
          ) : (
            <img src={group.icon} alt={group.name} className="h-5 w-5 shrink-0 opacity-60 group-hover:opacity-90 dark:invert-[0.8]" />
          )}
          <span className="truncate">{group.name}</span>
        </div>
        {isOpen ? <ChevronDownIcon className="h-4 w-4 text-slate-400 dark:text-[#6e7681] shrink-0" /> : <ChevronRightIcon className="h-4 w-4 text-slate-400 dark:text-[#6e7681] shrink-0" />}
      </div>
      
      <div 
        className={`overflow-hidden transition-all duration-200 ease-in-out ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="ml-4 pl-3 border-l border-slate-200/80 dark:border-[#30363d] space-y-0.5 my-1">
          {group.children?.map((child) => {
            // 🟢 THE FIX: Require exact match for the base /admin/workforce route
            const currentFull = location.pathname + location.search;
            const active = child.path.includes('?')
              ? currentFull === child.path
              : (child.path === "/admin/workforce" 
                  ? location.pathname === child.path
                  : location.pathname === child.path || location.pathname.startsWith(child.path + "/"));
              
            return (
              <SidebarLink 
                key={child.name} 
                name={child.name} 
                icon={null}
                IconComponent={child.IconComponent}
                path={child.path} 
                active={active}
                scrollContainerRef={scrollContainerRef}
                closeSidebar={closeSidebar}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- MAIN SIDEBAR ---
const Sidebar = ({ isOpen, closeSidebar }) => {
  const location = useLocation();
  const { user, checkAccess } = useAuth(); 
  const { activeModules } = useContext(SettingsContext); 
  const sidebarRef = useRef(null);

  useEffect(() => {
    const restoreScroll = () => {
      const savedScroll = sessionStorage.getItem('sidebar_scroll');
      if (savedScroll && sidebarRef.current) {
        sidebarRef.current.scrollTop = parseInt(savedScroll, 10);
      }
    };
    restoreScroll();
  }, []);

  const menuItems = [
    { 
      type: "link", name: "Dashboard", IconComponent: Squares2X2Icon, path: "/admin/dashboard",
      permission: null,
      adminOnly: true // Internal company KPI dashboard, hidden from customers
    },
    { 
      type: "link", 
      name: "Site Operations", 
      IconComponent: HardHatIcon, 
      path: "/admin/customer-portal",
      permission: 'customer.portal'
    },
    { 
      type: "link", name: "CRM", IconComponent: UserGroupIcon, path: "/crm",
      permission: 'crm.view', module: 'crm'
    },
    { 
      type: "link", name: "Projects", IconComponent: HardHatIcon, path: "/admin/projects",
      permission: 'project.view', module: 'projects'
    },
    { 
      type: "link", name: "Tasks", IconComponent: ClipboardDocumentCheckIcon, path: "/admin/Task",
      permission: 'task.view', module: 'tasks'
    },
    { 
      type: "group", 
      name: "Finance & Accounts", 
      IconComponent: BanknotesIcon, 
      permission: 'financial.view',
      module: 'accounting', 
      children: [
        { name: "Overview", path: "/admin/accounting/dashboard", permission: 'financial.view', IconComponent: ChartBarIcon },
        { name: "Budgeting", path: "/admin/BudgetPage", permission: 'financial.view', IconComponent: ClipboardDocumentListIcon },
        { name: "Customer Invoices", path: "/admin/accounting/invoices", permission: 'accounting.invoices', IconComponent: ReceiptPercentIcon },
        { name: "Vendor Bills", path: "/admin/accounting/bills", permission: 'accounting.bills', IconComponent: BuildingLibraryIcon },
        { name: "General Ledger", path: "/admin/accounting/ledger", permission: 'accounting.reports', IconComponent: ScaleIcon },
        { name: "Budget vs Actual", path: "/admin/accounting/reports/budget", permission: 'accounting.reports', IconComponent: ChartBarIcon },
      ]
    },
    { 
      type: "group", 
      name: "Materials", 
      IconComponent: CubeIcon, 
      permission: null, 
      module: 'materials', 
      children: [
        { name: "Purchase", path: "/admin/material/purchase", permission: 'purchase.view', IconComponent: ShoppingCartIcon },
        { name: "Internal Picking", path: "/admin/material/internal-picking", permission: 'inventory.picking', IconComponent: ArrowPathIcon },
        { name: "Scrap", path: "/admin/material/scrap", permission: 'inventory.scrap', IconComponent: TrashIcon },
      ]
    },
    // 🟢 HR & WORKFORCE MODULE
    { 
      type: "group", 
      name: "HR & Workforce", 
      IconComponent: UserGroupIcon, 
      permission: 'workforce.view', 
      module: 'workforce', 
      children: [
        // Admin-only: full employee list & management
        { name: "Employees", path: "/admin/workforce", permission: 'workforce.view', IconComponent: UsersIcon, adminOnly: true },
        // Admin: all attendance records
        { name: "Attendance", path: "/admin/workforce/attendance", permission: 'workforce.view', IconComponent: ClockIcon, adminOnly: true },
        { name: "Shift Roster", path: "/admin/workforce/shifts", permission: 'workforce.view', IconComponent: CalendarIcon, adminOnly: true },
        { name: "Leave Management", path: "/admin/workforce/leaves", permission: 'workforce.view', IconComponent: ClipboardDocumentListIcon, adminOnly: true },
        { name: "Leave Balance", path: "/admin/workforce/leave-balances", permission: 'workforce.edit', IconComponent: ClipboardDocumentListIcon, adminOnly: true },
        { name: "Payroll Admin", path: "/admin/workforce/payroll", permission: 'workforce.edit', IconComponent: BanknotesIcon, adminOnly: true },
        // Worker self-service items (shown ONLY to workers, hidden from admins)
        { name: "My Attendance", path: "/admin/workforce/attendance", permission: 'attendance.self', IconComponent: ClockIcon, workerOnly: true },
        { name: "My Payslips", path: "/admin/workforce/my-payslips", permission: 'payslip.self', IconComponent: DocumentTextIcon, workerOnly: true },
        { name: "My Leaves", path: "/admin/workforce/leaves", permission: 'leave.self', IconComponent: CalendarIcon, workerOnly: true },
      ]
    },
    { 
      type: "link", name: "Vendors", IconComponent: BuildingStorefrontIcon, path: "/admin/VendorPage",
      permission: 'vendor.view', module: 'vendor'
    },
    { 
      type: "link", name: "Inventory", IconComponent: ArchiveBoxIcon, path: "/admin/Inventory",
      permission: 'inventory.view', module: 'inventory'
    },
    {
      type: 'link', name: 'Warehouse', path: '/admin/warehouse-management', IconComponent: BuildingLibraryIcon, 
      permission: 'warehouse.view', module: 'warehouse'
    },
    { 
      type: "link", name: "Assets", IconComponent: WrenchScrewdriverIcon, path: "/admin/assets",
      permission: 'asset.view', module: 'assets'
    },
    { 
      type: "link", name: "Documents", IconComponent: DocumentTextIcon, path: "/admin/DocumentPage",
      permission: 'document.view', module: 'documents'
    },
    { 
      type: "link", name: "Compliance", IconComponent: ShieldCheckIcon, path: "/admin/Compliance-Safety",
      permission: 'compliance.view', module: 'compliance'
    },
    { 
      type: "link", name: "Reports", IconComponent: ChartBarIcon, path: "/admin/Reporting-Analytics",
      permission: 'report.view', module: 'reporting'
    },
    { 
      type: "group", 
      name: "User Management", 
      IconComponent: UserGroupIcon, 
      permission: null, 
      children: [
        { name: "Admin Users", path: "/admin/users", permission: 'user.view', IconComponent: UsersIcon },
        { name: "Roles & Rights", path: "/admin/roles", permission: 'role.view', IconComponent: ShieldCheckIcon },
        { name: "Subscriptions & Trials", path: "/admin/subscriptions", permission: 'user.view', IconComponent: BanknotesIcon },
      ]
    },
    { 
      type: "link", 
      name: "Settings", 
      IconComponent: Cog6ToothIcon,
      path: "/admin/settings",
      permission: 'settings.view'
    },
  ];

  return (
    <div 
      ref={sidebarRef}
      className={`fixed top-16 left-0 bottom-0 w-64 bg-white dark:bg-[#161b22] text-slate-800 dark:text-slate-200 border-r border-slate-200/80 dark:border-[#30363d] flex flex-col z-50 overflow-y-auto [&::-webkit-scrollbar]:hidden transition-transform duration-300 ease-in-out shadow-xs ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* <div className="px-4 py-3 border-b border-slate-100 dark:border-[#30363d] flex items-center gap-2.5 bg-slate-50/70 dark:bg-[#21262d]/50">
        <div className="w-7 h-7 rounded-lg bg-[#D9531E]/10 border border-[#D9531E]/20 flex items-center justify-center shrink-0">
          <span className="font-black text-[#D9531E] text-xs">B</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-900 dark:text-white tracking-tight truncate font-display">BriqERP Console</p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wider">Enterprise Suite</p>
        </div>
      </div> */}
      <div className="px-4 pt-3 pb-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Workspace</p>
      </div>
      <nav className="flex-1 px-3 pb-4 space-y-0.5">
        {menuItems.map((item, index) => {
          if (item.module && activeModules && activeModules[item.module] === false) {
             return null;
          }

          if (item.type === "group") {
            const safeCheck = (perm) => checkAccess ? checkAccess(perm) : false;
            
            const visibleChildren = item.children.filter(child => {
              const hasPermission = safeCheck(child.permission);
              if (!hasPermission) return false;

              const isAdmin = safeCheck('workforce.edit') || safeCheck('user.manage');

              // adminOnly items: hide from workers (those without any admin permission)
              if (child.adminOnly && !isAdmin) return false;

              // workerOnly items: hide from admins (they have workforce.edit)
              if (child.workerOnly && isAdmin) return false;

              return true;
            });

            if (visibleChildren.length === 0) return null; 

            const filteredGroup = { ...item, children: visibleChildren };
            return <SidebarGroup key={index} group={filteredGroup} location={location} scrollContainerRef={sidebarRef} closeSidebar={closeSidebar} />;
          } 
          else {
            const safeCheck = (perm) => checkAccess ? checkAccess(perm) : false;
            if (!safeCheck(item.permission)) return null; 

            const isAdmin = safeCheck('workforce.edit') || safeCheck('user.manage') || (user?.role === 'Super Admin' || user?.role === 'Admin');
            if (item.adminOnly && !isAdmin) return null;

            const currentPath = location.pathname.toLowerCase();
            const targetPath = item.path.toLowerCase();
            const active = currentPath === targetPath || currentPath.startsWith(targetPath + "/");
            return <SidebarLink key={index} name={item.name} icon={item.icon} IconComponent={item.IconComponent} path={item.path} active={active} scrollContainerRef={sidebarRef} closeSidebar={closeSidebar} />;
          }
        })}
      </nav>
    </div>
  );
};

export default Sidebar;